import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const ALL_ROUTES = [
  { name: 'Dashboard Taller', path: '/' },
  { name: 'Sign In', path: '/signin' },
  { name: 'Dashboard (alias)', path: '/workshop-dashboard' },
  { name: 'Recepción de Unidad', path: '/vehicle-reception' },
  { name: 'Clientes', path: '/clients-management' },
  { name: 'Vehículos (Archivo)', path: '/vehicle-archive' },
  { name: 'Historial de Vehículos', path: '/vehicle-history' },
  { name: 'Solicitudes', path: '/solicitudes-management' },
  { name: 'Cotización', path: '/quotation-management' },
  { name: 'Citas / Calendario', path: '/operational-calendar' },
  { name: 'Portal Cliente (Login)', path: '/portal/login' },
  { name: 'Inspección Técnica', path: '/technical-inspection' },
  { name: 'Línea de Tiempo OT', path: '/work-order-timeline' },
  { name: 'Ventas / Notas', path: '/sales-management' },
  { name: 'Ejecución de Trabajo', path: '/work-execution' },
  { name: 'Entrega de Vehículo', path: '/vehicle-delivery-confirmation' },
  { name: 'Reportes Avanzados', path: '/advanced-reporting' },
  { name: 'Analítica de Clientes', path: '/client-analytics' },
  { name: 'CRM', path: '/crm-management' },
  { name: 'Catálogo de Productos', path: '/product-catalog' },
  { name: 'Proveedores', path: '/providers-management' },
  { name: 'Coordinación Proveedores', path: '/supplier-coordination' },
  { name: 'Gestión de Emails', path: '/email-management' },
  { name: 'Plantillas de Email', path: '/email-template-agement' },
  { name: 'Reglas de Automatización', path: '/automation-rules-management' },
  { name: 'Analítica de Automatización', path: '/automation-analytics-dashboard' },
  { name: 'Feedback de Clientes', path: '/customer-feedback' },
  { name: 'Branding / Empresa', path: '/company-branding' },
  { name: 'Accesos Portal', path: '/accesos-portal' },
  { name: 'Bitácora de Auditoría', path: '/audit-log' },
];

const STATUS_LABELS = {
  pending: { label: '—', color: 'text-gray-400 bg-gray-50', dot: 'bg-gray-300' },
  ok: { label: 'OK', color: 'text-green-700 bg-green-50', dot: 'bg-green-500' },
  error: { label: 'Error', color: 'text-red-700 bg-red-50', dot: 'bg-red-500' },
  loading: { label: 'Cargando demasiado', color: 'text-yellow-700 bg-yellow-50', dot: 'bg-yellow-400' },
  empty: { label: 'Ruta vacía', color: 'text-orange-700 bg-orange-50', dot: 'bg-orange-400' },
  redirect: { label: 'Redirección incorrecta', color: 'text-purple-700 bg-purple-50', dot: 'bg-purple-400' },
  fixed: { label: 'Corregido ✓', color: 'text-teal-700 bg-teal-50', dot: 'bg-teal-500' },
};

const TIMEOUT_MS = 4000;

function probeRoute(path) {
  return new Promise((resolve) => {
    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:1px;height:1px;opacity:0;pointer-events:none;';
    const origin = window.location.origin;
    const fullUrl = `${origin}${path}`;
    let settled = false;
    const timer = setTimeout(() => {
      if (!settled) {
        settled = true;
        document.body.removeChild(iframe);
        resolve('loading');
      }
    }, TIMEOUT_MS);
    iframe.onload = () => {
      if (!settled) {
        settled = true;
        clearTimeout(timer);
        try {
          const loc = iframe.contentWindow?.location?.href || '';
          const iframePathname = new URL(loc).pathname;
          if (iframePathname === '/signin' && path !== '/signin') {
            document.body.removeChild(iframe);
            resolve('redirect');
            return;
          }
          const body = iframe.contentDocument?.body?.innerText?.trim() || '';
          if (body.length < 20) {
            document.body.removeChild(iframe);
            resolve('empty');
            return;
          }
          document.body.removeChild(iframe);
          resolve('ok');
        } catch {
          document.body.removeChild(iframe);
          resolve('ok');
        }
      }
    };
    iframe.onerror = () => {
      if (!settled) {
        settled = true;
        clearTimeout(timer);
        document.body.removeChild(iframe);
        resolve('error');
      }
    };
    iframe.src = fullUrl;
    document.body.appendChild(iframe);
  });
}

export default function QARoutes() {
  const navigate = useNavigate();
  const [statuses, setStatuses] = useState(() =>
    Object.fromEntries(ALL_ROUTES?.map((r) => [r?.path, 'pending']))
  );
  const [testing, setTesting] = useState(false);
  const [testTime, setTestTime] = useState(null);
  const [copied, setCopied] = useState(false);

  // --- Ver destino del click state ---
  const [clickMode, setClickMode] = useState(false);
  const [clickInfo, setClickInfo] = useState(null);
  const clickInfoRef = useRef(null);

  // Intercept clicks globally when clickMode is active
  useEffect(() => {
    if (!clickMode) return;

    const handler = (e) => {
      // Find the closest interactive element
      const target = e?.target;
      const interactive = target?.closest('button, a, [role="button"], [role="link"], [onClick], .cursor-pointer, [data-action]');
      const el = interactive || target;

      // Gather info
      let destination = null;
      let actionLabel = null;
      let elementType = el?.tagName?.toLowerCase() || 'element';

      // Check for href (links)
      const href = el?.getAttribute('href') || el?.closest('a')?.getAttribute('href');
      if (href) {
        destination = href;
      }

      // Check for react-router navigate via data attributes or aria
      const ariaLabel = el?.getAttribute('aria-label') || el?.closest('[aria-label]')?.getAttribute('aria-label');
      const dataAction = el?.getAttribute('data-action') || el?.closest('[data-action]')?.getAttribute('data-action');
      const title = el?.getAttribute('title') || el?.closest('[title]')?.getAttribute('title');

      // Try to extract text content
      const textContent = (el?.textContent || '')?.trim()?.slice(0, 80);

      // Try to detect onClick handler name from React fiber
      let reactAction = null;
      try {
        const fiberKey = Object.keys(el)?.find(k => k?.startsWith('__reactFiber') || k?.startsWith('__reactInternalInstance'));
        if (fiberKey) {
          let fiber = el?.[fiberKey];
          let depth = 0;
          while (fiber && depth < 10) {
            const props = fiber?.memoizedProps || fiber?.pendingProps;
            if (props?.onClick) {
              const fnStr = props?.onClick?.toString();
              // Extract function name or first meaningful line
              const match = fnStr?.match(/(?:function\s+(\w+)|(\w+)\s*=>|navigate\(['"`]([^'"`]+)['"`]\))/);
              if (match) {
                reactAction = match?.[1] || match?.[2] || (match?.[3] ? `navigate("${match?.[3]}")` : null);
              }
              // Check for navigate calls
              const navMatch = fnStr?.match(/navigate\(['"`]([^'"`]+)['"`]\)/);
              if (navMatch && !destination) {
                destination = navMatch?.[1];
              }
              break;
            }
            fiber = fiber?.return;
            depth++;
          }
        }
      } catch (_) {}

      actionLabel = dataAction || ariaLabel || title || reactAction || null;

      const info = {
        destination: destination || (href ? href : null),
        action: actionLabel,
        text: textContent || '(sin texto)',
        elementType,
        classes: (el?.className || '')?.toString()?.split(' ')?.filter(Boolean)?.slice(0, 5)?.join(' '),
        tagName: el?.tagName,
        id: el?.id || null,
      };

      setClickInfo(info);
      e?.preventDefault();
      e?.stopPropagation();
    };

    document.addEventListener('click', handler, true);
    return () => document.removeEventListener('click', handler, true);
  }, [clickMode]);

  // Close popup on Escape
  useEffect(() => {
    const onKey = (e) => {
      if (e?.key === 'Escape') setClickInfo(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const toggleClickMode = () => {
    setClickMode((v) => {
      if (v) setClickInfo(null);
      return !v;
    });
  };

  const setStatus = (path, status) =>
    setStatuses((prev) => ({ ...prev, [path]: status }));

  const testSingle = useCallback(async (path) => {
    setStatus(path, 'pending');
    const result = await probeRoute(path);
    setStatus(path, result);
  }, []);

  const testAll = useCallback(async () => {
    setTesting(true);
    setTestTime(null);
    const reset = Object.fromEntries(ALL_ROUTES?.map((r) => [r?.path, 'pending']));
    setStatuses(reset);
    for (const route of ALL_ROUTES) {
      setStatus(route?.path, 'pending');
      const result = await probeRoute(route?.path);
      setStatus(route?.path, result);
    }
    setTestTime(new Date()?.toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'medium' }));
    setTesting(false);
  }, []);

  const markFixed = (path) => setStatus(path, 'fixed');

  const counts = ALL_ROUTES?.reduce(
    (acc, r) => {
      const s = statuses?.[r?.path];
      if (s === 'ok' || s === 'fixed') acc.ok++;
      else if (s !== 'pending') acc.fail++;
      return acc;
    },
    { ok: 0, fail: 0 }
  );

  const generateReport = () => {
    const lines = [
      `=== Reporte QA Rutas — Gearcontrol ===`,
      `Hora: ${testTime || 'No ejecutado'}`,
      '',
      ...ALL_ROUTES?.map((r) => {
        const s = statuses?.[r?.path];
        const label = STATUS_LABELS?.[s]?.label || s;
        return `[${label}] ${r?.name} → ${r?.path}`;
      }),
      '',
      `Correctas: ${counts?.ok} | Fallando: ${counts?.fail}`,
    ];
    return lines?.join('\n');
  };

  const copyReport = async () => {
    await navigator.clipboard?.writeText(generateReport());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Click Mode Global Overlay Banner */}
      {clickMode && (
        <div className="fixed top-0 left-0 right-0 z-[9998] bg-amber-500 text-white text-center py-2 text-sm font-semibold shadow-lg flex items-center justify-center gap-3">
          <span className="animate-pulse w-2.5 h-2.5 rounded-full bg-white inline-block" />
          Modo "Ver destino del click" activo — haz click en cualquier botón o tarjeta
          <button
            onClick={toggleClickMode}
            className="ml-3 px-3 py-0.5 bg-white text-amber-700 rounded-full text-xs font-bold hover:bg-amber-100 transition-colors"
          >
            Desactivar
          </button>
        </div>
      )}
      {/* Click Destination Popup */}
      {clickInfo && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center" onClick={() => setClickInfo(null)}>
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
          <div
            className="relative bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-md mx-4 p-6"
            onClick={(e) => e?.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center text-amber-600 text-lg">🎯</span>
                <h2 className="text-base font-bold text-gray-900">Destino del click</h2>
              </div>
              <button
                onClick={() => setClickInfo(null)}
                className="text-gray-400 hover:text-gray-600 text-xl leading-none"
              >
                ×
              </button>
            </div>

            {/* Info rows */}
            <div className="space-y-3">
              {/* Destination / Route */}
              <div className="rounded-lg bg-gray-50 border border-gray-200 p-3">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Ruta / Destino</div>
                {clickInfo?.destination ? (
                  <span className="font-mono text-sm font-bold text-blue-700 break-all">{clickInfo?.destination}</span>
                ) : (
                  <span className="text-sm text-gray-400 italic">Sin ruta detectada (acción interna)</span>
                )}
              </div>

              {/* Action */}
              {clickInfo?.action && (
                <div className="rounded-lg bg-blue-50 border border-blue-100 p-3">
                  <div className="text-xs font-semibold text-blue-500 uppercase tracking-wide mb-1">Acción</div>
                  <span className="font-mono text-sm text-blue-800 break-all">{clickInfo?.action}</span>
                </div>
              )}

              {/* Element text */}
              <div className="rounded-lg bg-gray-50 border border-gray-200 p-3">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Texto del elemento</div>
                <span className="text-sm text-gray-700 break-all">{clickInfo?.text}</span>
              </div>

              {/* Element details */}
              <div className="rounded-lg bg-gray-50 border border-gray-200 p-3">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Detalles del elemento</div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-gray-400">Tipo: </span>
                    <span className="font-mono text-gray-700">{clickInfo?.tagName?.toLowerCase()}</span>
                  </div>
                  {clickInfo?.id && (
                    <div>
                      <span className="text-gray-400">ID: </span>
                      <span className="font-mono text-gray-700">#{clickInfo?.id}</span>
                    </div>
                  )}
                  {clickInfo?.classes && (
                    <div className="col-span-2">
                      <span className="text-gray-400">Clases: </span>
                      <span className="font-mono text-gray-600 break-all">{clickInfo?.classes}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="mt-4 flex items-center justify-between">
              <p className="text-xs text-gray-400">Presiona Esc o haz click fuera para cerrar</p>
              <button
                onClick={() => setClickInfo(null)}
                className="px-4 py-1.5 bg-gray-900 text-white text-xs font-medium rounded-lg hover:bg-gray-700 transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Header */}
      <div className={`max-w-5xl mx-auto ${clickMode ? 'mt-10' : ''}`}>
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-red-100 text-red-700 uppercase tracking-wide">
                Solo lectura · Diagnóstico interno
              </span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mt-1">QA — Validación de Rutas</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Verifica que las rutas principales de Gearcontrol rendericen correctamente.
            </p>
          </div>
          <button
            onClick={() => navigate(-1)}
            className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
          >
            ← Volver
          </button>
        </div>

        {/* Counter bar */}
        <div className="flex items-center gap-4 mt-4 mb-4 p-3 bg-white rounded-lg border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-green-500 inline-block" />
            <span className="text-sm font-medium text-gray-700">Correctas: <strong className="text-green-700">{counts?.ok}</strong></span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-red-500 inline-block" />
            <span className="text-sm font-medium text-gray-700">Fallando: <strong className="text-red-700">{counts?.fail}</strong></span>
          </div>
          {testTime && (
            <span className="ml-auto text-xs text-gray-400">Última prueba: {testTime}</span>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex flex-wrap gap-2 mb-5">
          <button
            onClick={testAll}
            disabled={testing}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2"
          >
            {testing ? (
              <>
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
                Probando...
              </>
            ) : (
              '▶ Probar todos'
            )}
          </button>
          <button
            onClick={copyReport}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-800 text-white text-sm font-medium rounded-lg transition-colors"
          >
            {copied ? '✓ Copiado' : '📋 Copiar reporte'}
          </button>
          {/* Ver destino del click toggle */}
          <button
            onClick={toggleClickMode}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-all flex items-center gap-2 border ${
              clickMode
                ? 'bg-amber-500 hover:bg-amber-600 text-white border-amber-500 shadow-md'
                : 'bg-white hover:bg-amber-50 text-amber-700 border-amber-300'
            }`}
          >
            <span>🎯</span>
            {clickMode ? 'Desactivar modo click' : 'Ver destino del click'}
            {clickMode && (
              <span className="ml-1 w-2 h-2 rounded-full bg-white animate-pulse inline-block" />
            )}
          </button>
        </div>

        {/* Route table */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 font-semibold text-gray-600 w-1/4">Módulo</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600 w-1/3">Ruta</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600 w-1/4">Estado</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {ALL_ROUTES?.map((route) => {
                const s = statuses?.[route?.path];
                const info = STATUS_LABELS?.[s] || STATUS_LABELS?.pending;
                return (
                  <tr key={route?.path} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 font-medium text-gray-800">{route?.name}</td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-500">{route?.path}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${info?.color}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${info?.dot}`} />
                        {info?.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => window.open(route?.path, '_blank')}
                          className="px-3 py-1 text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-md font-medium transition-colors"
                        >
                          Abrir
                        </button>
                        <button
                          onClick={() => testSingle(route?.path)}
                          disabled={testing}
                          className="px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-md font-medium transition-colors disabled:opacity-40"
                        >
                          Probar
                        </button>
                        {s !== 'pending' && s !== 'fixed' && (
                          <button
                            onClick={() => markFixed(route?.path)}
                            className="px-3 py-1 text-xs bg-teal-50 hover:bg-teal-100 text-teal-700 rounded-md font-medium transition-colors"
                          >
                            Marcar corregido
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Summary report */}
        {testTime && (
          <div className="mt-6 bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <h2 className="text-sm font-semibold text-gray-700 mb-3">Resumen del reporte</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
              {[
                { label: 'Correctas', key: ['ok', 'fixed'], color: 'text-green-700 bg-green-50 border-green-200' },
                { label: 'Spinner / Timeout', key: ['loading'], color: 'text-yellow-700 bg-yellow-50 border-yellow-200' },
                { label: 'Rutas vacías', key: ['empty'], color: 'text-orange-700 bg-orange-50 border-orange-200' },
                { label: 'Redirect roto', key: ['redirect'], color: 'text-purple-700 bg-purple-50 border-purple-200' },
                { label: 'Con error', key: ['error'], color: 'text-red-700 bg-red-50 border-red-200' },
              ]?.map(({ label, key, color }) => {
                const count = ALL_ROUTES?.filter((r) => key?.includes(statuses?.[r?.path]))?.length;
                return (
                  <div key={label} className={`rounded-lg border p-3 ${color}`}>
                    <div className="text-2xl font-bold">{count}</div>
                    <div className="text-xs font-medium mt-0.5">{label}</div>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-gray-400">Prueba ejecutada: {testTime}</p>
          </div>
        )}
      </div>
    </div>
  );
}
